export type MediaPreferences = {
  readonly muted: boolean;
  readonly volume: number;
};

export const MEDIA_PREFERENCES_STORAGE_KEY =
  "kalkonen.world:media-preferences:v1";

export const defaultMediaPreferences: MediaPreferences = {
  muted: true,
  volume: 0.15,
};

function browserStorage(): Storage | null {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function clampVolume(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function parsePreferences(value: unknown): MediaPreferences {
  if (!value || typeof value !== "object") return defaultMediaPreferences;

  const candidate = value as Record<string, unknown>;
  const muted =
    typeof candidate.muted === "boolean"
      ? candidate.muted
      : defaultMediaPreferences.muted;
  const volume =
    typeof candidate.volume === "number" && Number.isFinite(candidate.volume)
      ? clampVolume(candidate.volume)
      : defaultMediaPreferences.volume;

  return { muted, volume };
}

export function readMediaPreferences(): MediaPreferences {
  const storage = browserStorage();
  if (!storage) return defaultMediaPreferences;

  try {
    const storedValue = storage.getItem(MEDIA_PREFERENCES_STORAGE_KEY);
    if (!storedValue) return defaultMediaPreferences;
    return parsePreferences(JSON.parse(storedValue));
  } catch {
    return defaultMediaPreferences;
  }
}

export function writeMediaPreferences(preferences: MediaPreferences): void {
  const storage = browserStorage();
  if (!storage) return;

  try {
    storage.setItem(
      MEDIA_PREFERENCES_STORAGE_KEY,
      JSON.stringify(parsePreferences(preferences)),
    );
  } catch {
    // Storage may be unavailable or full; media controls still work in memory.
  }
}
